import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Wine, Heart, Star } from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1607622750671-6cd9a99eabd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY29ja3RhaWwlMjBwcmVwYXJhdGlvbnxlbnwwfHx8fDE3ODM5MzE1MTV8MA&ixlib=rb-4.1.0&q=85";

function Hero() {
  return (
    <section className="relative min-h-[92vh] hero-radial overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-52 pb-24 relative">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-8 animate-fade-up">
            <span className="h-px w-10 bg-primary" />
            <span className="overline text-primary">Mobile Cocktail Bar</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[1.05] font-light animate-fade-up" data-testid="hero-title">
            Elegant cocktails.
            <br />
            <span className="italic text-primary">Delivered</span> to your event.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
            Pinkies Up brings a full mobile cocktail experience to weddings, birthdays,
            and celebrations that deserve more than a bottle of prosecco.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row items-start gap-4 animate-fade-up" style={{ animationDelay: "220ms" }}>
            <Link
              to="/book"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-3 rounded-full px-7 py-4 bg-primary text-primary-foreground font-medium tracking-wide hover:bg-primary/90 transition-colors pink-glow"
            >
              Book your date <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/packages"
              data-testid="hero-packages-cta"
              className="inline-flex items-center gap-3 rounded-full px-7 py-4 border border-white/15 text-foreground/90 hover:border-primary/40 hover:text-primary transition-colors"
            >
              View packages
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg animate-fade-up" style={{ animationDelay: "320ms" }}>
            {[
              { n: "150+", l: "Events served" },
              { n: "4.9★", l: "Client rating" },
              { n: "24hr", l: "Booking reply" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl">{s.n}</div>
                <div className="overline mt-2 text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueProps() {
  const items = [
    { icon: Sparkles, title: "Signature Menus", copy: "Custom cocktail lists designed around your event, aesthetic, and guests." },
    { icon: Wine, title: "Craft Mixologists", copy: "Trained bartenders bringing bar-quality drinks to your venue, home, or dance floor." },
    { icon: Heart, title: "Turnkey Setup", copy: "We arrive, we style the bar, we serve, we clean up. You enjoy the party." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="value-props">
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((it) => (
          <div key={it.title} className="rounded-2xl border border-white/8 bg-card/60 p-8 card-lift">
            <it.icon size={22} className="text-primary" strokeWidth={1.5} />
            <h3 className="font-display text-2xl mt-6">{it.title}</h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">{it.copy}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PackagesPreview() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPkgs(r.data)).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" id="packages" data-testid="packages-preview">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div className="max-w-2xl">
          <div className="overline text-primary/80">Packages</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">Pick your evening.</h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Three curated experiences — from intimate soirées to grand affairs. Every package is fully customisable.
          </p>
        </div>
        <Link to="/packages" className="text-primary hover:underline text-sm" data-testid="see-all-packages">
          See all packages →
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {pkgs.map((p) => (
          <article
            key={p.id}
            className={`relative rounded-2xl overflow-hidden border card-lift ${
              p.is_featured ? "border-primary/40" : "border-white/8"
            } bg-card/60`}
            data-testid={`package-card-${p.id}`}
          >
            {p.is_featured && (
              <div className="absolute top-4 right-4 z-10 rounded-full bg-primary text-primary-foreground text-[10px] tracking-widest uppercase px-3 py-1">
                Most booked
              </div>
            )}
            <div className="aspect-[4/3] overflow-hidden">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <div className="overline text-primary/70">{p.tagline}</div>
              <h3 className="mt-3 font-display text-2xl">{p.name}</h3>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{p.description}</p>
              <div className="mt-6 flex items-end justify-between">
                <div>
                  <div className="font-display text-3xl">£{p.price}</div>
                  <div className="overline text-muted-foreground mt-1">{p.duration_hours} hrs · {p.guest_count}</div>
                </div>
                <Link
                  to={`/book?package=${p.id}`}
                  data-testid={`package-book-${p.id}`}
                  className="rounded-full px-4 py-2 text-xs tracking-widest uppercase border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Book
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    { name: "Amelia & Ryan", role: "Wedding, Sonoma", quote: "The pink-hued signature drinks were the talk of our wedding. Everything felt curated — from the glassware to the garnish." },
    { name: "Priya Patel", role: "30th Birthday", quote: "Pinkies Up turned my backyard into a boutique cocktail lounge. The bartenders were unreal and the menu was so on-brand for me." },
    { name: "Nova Studios", role: "Brand launch", quote: "Effortless setup, thoughtful details. Guests kept asking who catered the bar. Booking them again for our next launch." },
  ];
  return (
    <section className="py-24 bg-secondary/30 border-y border-white/5" data-testid="testimonials">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="overline text-primary/80">Client stories</div>
        <h2 className="mt-4 font-display text-4xl md:text-5xl max-w-2xl">Loved by the people who host the best parties.</h2>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((t) => (
            <blockquote key={t.name} className="rounded-2xl border border-white/8 p-8 bg-background/40 card-lift">
              <div className="flex gap-1 text-primary">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="mt-6 text-lg leading-relaxed">“{t.quote}”</p>
              <footer className="mt-8">
                <div className="font-medium">{t.name}</div>
                <div className="overline text-muted-foreground mt-1">{t.role}</div>
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="cta-section">
      <div className="rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/10 to-transparent p-10 md:p-16 relative overflow-hidden">
        <div className="max-w-2xl relative z-10">
          <div className="overline text-primary/80">Ready when you are</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">Let's plan your event.</h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Pick a date, choose a package, and we'll take care of the rest.
          </p>
          <Link
            to="/book"
            data-testid="cta-book-now"
            className="mt-10 inline-flex items-center gap-3 rounded-full px-7 py-4 bg-primary text-primary-foreground font-medium tracking-wide hover:bg-primary/90 transition-colors pink-glow"
          >
            Start your booking <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <ValueProps />
      <PackagesPreview />
      <Testimonials />
      <CTA />
    </div>
  );
}
